import * as Sentry from '@sentry/react';

import type { CapturedNetworkRequest, PostHog, Properties } from 'posthog-js';

const redactSensitiveData = (body: string): string => {
  return body
    .replace(/"password":\s*"[^"]*"/g, '"password": "***"')
    .replace(/"token":\s*"[^"]*"/g, '"token": "***"')
    .replace(/"apiKey":\s*"[^"]*"/g, '"apiKey": "***"')
    .replace(/"api_key":\s*"[^"]*"/g, '"api_key": "***"')
    .replace(/"secret":\s*"[^"]*"/g, '"secret": "***"')
    .replace(/"authorization":\s*"[^"]*"/g, '"authorization": "***"');
};

const options: Record<string, unknown> = {
  api_host: '/relay-KKIZ/',
  ui_host: import.meta.env.VITE_PUBLIC_POSTHOG_UI_HOST,
  enable_recording_console_log: true,
  capture_performance: true,
  session_recording: {
    recordBody: true,
    recordCrossOriginIframes: true,
    maskCapturedNetworkRequestFn: (request: CapturedNetworkRequest) => {
      if (request.requestBody) {
        request.requestBody = redactSensitiveData(request.requestBody);
      }

      if (request.responseBody) {
        request.responseBody = redactSensitiveData(request.responseBody);
      }

      return request;
    },
  },
};

const isLocalhost =
  import.meta.env.VITE_SENTRY_ENVIRONMENT === 'localhost' ||
  window.location.hostname === 'localhost';

let client: PostHog | null = null;
let loading: Promise<void> | null = null;

// Calls made before the SDK finishes loading are replayed once it resolves;
// with analytics disabled `loading` stays null and every call is a no-op.
const whenReady = (run: (client: PostHog) => void) => {
  if (client) {
    run(client);
    return;
  }

  void loading?.then(() => {
    if (client) run(client);
  });
};

// __POSTHOG_ENABLED__ is a build-time literal (see vite.config.ts). When the
// key is absent it folds to `false`, so the dynamic import below is dropped
// and posthog-js never reaches the bundle.
export const initAnalytics = () => {
  if (__POSTHOG_ENABLED__ && !isLocalhost && !loading) {
    // The catch resolves rather than rethrows: a failed chunk fetch is
    // reported once and leaves `client` null, so every queued and later call
    // stays a silent no-op instead of raising its own unhandled rejection.
    loading = import('posthog-js')
      .then(({ default: posthog }) => {
        posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, options);
        client = posthog;
      })
      .catch((error) => {
        Sentry.captureException(error, {
          extra: { context: 'initAnalytics: failed to load posthog-js' },
        });
      });
  }
};

export const analytics = {
  capture: (event: string, properties?: Properties) =>
    whenReady((client) => client.capture(event, properties)),

  captureException: (error: unknown, properties?: Properties) =>
    whenReady((client) => client.captureException(error, properties)),

  identify: (distinctId?: string, properties?: Properties) =>
    whenReady((client) => client.identify(distinctId, properties)),

  reset: () => whenReady((client) => client.reset()),

  setCookiePersistence: (enabled: boolean) =>
    whenReady((client) =>
      client.set_config({
        persistence: enabled ? 'localStorage+cookie' : 'memory',
      }),
    ),
};
